import { CheckCircle2, Home } from "lucide-react";
import React from "react";
import { Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

function getDateLabel(selectedDate, granularity) {
  if (!selectedDate) return "";
  if (granularity === "day") {
    return new Date(selectedDate).toLocaleDateString();
  }
  if (granularity === "week") {
    // selectedDate: 'YYYY-Www'
    if (selectedDate.includes("-W")) {
      const [year, week] = selectedDate.split("-W");
      return `Week ${week}, ${year}`;
    }
    return selectedDate;
  }
  if (granularity === "month") {
    // selectedDate: 'YYYY-MM'
    const [year, month] = selectedDate.split("-");
    return `${new Date(year, month - 1).toLocaleString("default", {
      month: "long",
      year: "numeric",
    })}`;
  }
  if (granularity === "year") {
    return selectedDate.slice(0, 4);
  }
  if (granularity === "custom" && selectedDate.from && selectedDate.to) {
    const from = new Date(selectedDate.from).toLocaleDateString();
    const to = new Date(selectedDate.to).toLocaleDateString();
    return `${from} to ${to}`;
  }
  return "";
}

const ArrivalDepartureReport = ({
  arrivalList,
  departureList,
  selectedDate,
  granularity,
}) => {
  const navigate = useNavigate();
  const mappedArrivals = arrivalList?.map((item) => ({
    id: item.id,
    guestName: `${item.guest?.first_name || ""} ${item.guest?.last_name || ""}`,
    roomNumber: item.room_name || "N/A",
    checkInTime:
    new Date((item.check_in_date_time).split(".")[0]).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
    || "N/A",
    bookingId: item.booking_id,
    adults: item.adults || 1,
    children: item.children || 0,
    phone: item.guest?.phone || "N/A",
    purpose: item.purpose_of_visit || "N/A",
    status: item.booking_status || "confirmed",
  }));

  const mappedDepartures = departureList?.map((item) => ({
    id: item.id,
    guestName: `${item.guest?.first_name || ""} ${item.guest?.last_name || ""}`,
    roomNumber: item?.room_name || "N/A",
    checkOutTime:  new Date((item.check_out_date_time).split(".")[0]).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }),
    bookingId: item.booking_id,
    adults: item.adults || 1,
    children: item.children || 0,
    phone: item.guest?.phone || "N/A",
    folioBalance: parseFloat(item.total_amount || 0) + parseFloat(item.restaurant_charges || 0),
    status: item.booking_status || "checked_out",
  }));

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "check_in":
        return "badge bg-success";
      case "confirmed":
        return "badge bg-primary";
      case "check_out":
        return "badge bg-secondary";
      case "pending_checkout":
        return "badge bg-warning text-dark";
      default:
        return "badge bg-secondary";
    }
  };

  const formatStatus = (status) => {
    return status.replace("_", " ").toUpperCase();
  };

  const dateLabel = getDateLabel(selectedDate, granularity);

  const getStatusBadge = (status) => {
    if (status === "check_in") {
      return (
        <Badge variant="default" className="flex items-center gap-1">
          <Home className="w-3 h-3" />
          Checked In
        </Badge>
      );
    }

    return (
      <Badge variant="secondary" className="flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" />
        Checked Out
      </Badge>
    );
  };
  return (
    <div className="container-fluid">
      <div className="row g-4">
        {/* Arrivals Section */}
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header">
              <div className="d-flex align-items-center">
                <i
                  className="bi bi-calendar-date text-primary me-2"
                  style={{ fontSize: "1.2rem" }}
                ></i>
                <h5 className="card-title mb-0">Arrivals for {dateLabel}</h5>
              </div>
              <p className="card-text text-muted mb-0">
                Expected check-ins for {dateLabel}
              </p>
            </div>
            <div className="card-body">
              {mappedArrivals?.map((arrival) => (
                <div key={arrival.id} className="border rounded p-3 mb-3">
                  <div className="d-flex gap-3 align-items-center mb-3 py-1">
                    <h6 className="fw-bold mb-0">{arrival.guestName}</h6>
                    <span className={getStatusBadgeClass(arrival.status)}>
                      {formatStatus(arrival.status)}
                    </span>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-house-door-fill text-muted me-2"></i>
                        <small>Room {arrival.roomNumber}</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-clock text-muted me-2"></i>
                        <small>{arrival.checkInTime}</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-people text-muted me-2"></i>
                        <small>
                          {arrival.adults} Adults, {arrival.children} Children
                        </small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-telephone text-muted me-2"></i>
                        <small>{arrival.phone}</small>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                    <small className="text-muted">
                      Booking: {arrival.bookingId}
                      {/* • {arrival.purpose} */}
                    </small>
                    {/* {arrival.status === "confirmed" && (
                      <button className="btn btn-outline-primary btn-sm">
                        Check In
                      </button>
                    )} */}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Departures Section */}
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header">
              <div className="d-flex align-items-center">
                <i
                  className="bi bi-calendar-date text-warning me-2"
                  style={{ fontSize: "1.2rem" }}
                ></i>
                <h5 className="card-title mb-0">Departures for {dateLabel}</h5>
              </div>
              <p className="card-text text-muted mb-0">
                Expected check-outs for {dateLabel}
              </p>
            </div>
            <div className="card-body">
              {mappedDepartures?.map((departure) => (
                <div key={departure.id} className="border rounded p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex justify-content-between align-items-center gap-3">
                      <h6 className="fw-bold mb-0">{departure.guestName}</h6>
                      <span
                        className={getStatusBadgeClass(
                          departure.status === "check_in" ||
                            departure.status === "booked"
                            ? "pending_checkout"
                            : "check_out"
                        )}
                      >
                        {formatStatus(
                          departure.status === "check_in" ||
                            departure.status === "booked"
                            ? "Pending Checkout"
                            : "Checked Out"
                        )}
                      </span>
                    </div>
                    {(departure.status === "check_in") && (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() =>
                          navigate(
                            `/reservation/check-out-booking/${departure.id}`
                          )
                        }
                      >
                        <i className="bi bi-box-arrow-left me-1"></i>
                        Check Out
                      </button>
                    )}
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-house-door-fill text-muted me-2"></i>
                        <small>Room {departure.roomNumber}</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-clock text-muted me-2"></i>
                        <small>{departure.checkOutTime}</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-people text-muted me-2"></i>
                        <small>{departure.adults} Adults</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-telephone text-muted me-2"></i>
                        <small>{departure.phone}</small>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                    <small className="text-muted">
                      Booking: {departure.bookingId} • Balance: ₹
                      {departure.folioBalance}
                    </small>
                    {/* {departure.status === "pending_checkout" && (
                      <button className="btn btn-outline-primary btn-sm">
                        Check Out
                      </button>
                    )} */}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArrivalDepartureReport;
